import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import zeroneLogoDark from 'assets/images/svg/logo-dark.svg'
import InputField from 'components/common/InputField'
import PrimaryButton from 'components/common/PrimaryButton'
import SecondaryButton from 'components/common/SecondaryButton'
import ZeroneLoader from 'components/common/ZeroneLoader'
import AnimatedBackgroundContainer from 'components/common/AnimatedBackgroundContainer'
import { useToast } from 'provider/ToastProvider'
import { useAuth } from 'hooks/AuthContext'
import { sendEmailOtp, verifyEmailOtp, resendEmailOtp } from 'constants/api/endpoints/AuthEndpoints'
import styles from 'styles/components/LoginForm.module.sass'

const VerifyEmailPage: React.FC = () => {
    const [code, setCode]       = useState('')
    const [loading, setLoading] = useState(true)
    const [message, setMessage] = useState('Sending verification code...')
    const { addToast } = useToast()
    const { refreshUser } = useAuth()
    const navigate = useNavigate()

    useEffect(() => {
        void sendEmailOtp({ tag: 'VerifyEmailMount' }).then(result => {
            setLoading(false)
            if (result.alreadyVerified === true) {
                addToast({ message: 'Your email is already verified.', type: 'success' })
                void navigate('/')
            }
        }).catch(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleVerify = async (e: React.FormEvent) => {
        e.preventDefault()
        if (code.trim().length !== 6) {
            addToast({ message: 'Please enter the 6-digit code.', type: 'error' })
            return
        }
        setLoading(true)
        setMessage('Verifying...')
        const result = await verifyEmailOtp({ tag: 'VerifyEmail', data: { code: code.trim() } })
        setLoading(false)
        if (result.error) {
            addToast({ message: result.error, type: 'error' })
            return
        }
        addToast({ message: 'Email verified successfully!', type: 'success' })
        await refreshUser()
        void navigate('/')
    }

    const handleResend = async () => {
        setLoading(true)
        setMessage('Sending new code...')
        const result = await resendEmailOtp({ tag: 'ResendOtp' })
        setLoading(false)
        if (result.error) {
            addToast({ message: result.error, type: 'error' })
            return
        }
        addToast({ message: 'A new code has been sent to your email.', type: 'success' })
    }

    return (
        <AnimatedBackgroundContainer showFooter={true}>
            <form className={styles['login-form']} onSubmit={(e) => { void handleVerify(e) }}>
                <div className={styles['logo-wrapper']}>
                    <Link to="/"><img src={zeroneLogoDark} className={styles['logo']} alt="Zerone AnalytiQs Logo" /></Link>
                </div>
                {loading ? (
                    <ZeroneLoader text={message} />
                ) : (
                    <div className={styles['main-wrapper']}>
                        <div className={styles['input-wrapper']}>
                            <p style={{ fontSize: 14, color: '#374151', margin: '0 0 8px', textAlign: 'center' }}>
                                Enter the 6-digit code sent to your email.
                            </p>
                            <InputField
                                label="Verification Code"
                                type="text"
                                value={code}
                                onChange={setCode}
                                required
                            />
                        </div>
                        <div className={styles['action-buttons']}>
                            <PrimaryButton
                                text="Verify"
                                width="auto"
                                primaryColor="#FF5522"
                                textColor="#FFFFFF"
                                hoverColor="#FFFFFF"
                                hoverTextColor="#FF5522"
                                doSomething={(e: React.FormEvent) => { void handleVerify(e) }}
                            />
                            <SecondaryButton
                                text="Resend code"
                                width="auto"
                                primaryColor="transparent"
                                textColor="#ff5522"
                                hoverColor="#ff5522"
                                hoverTextColor="#FFFFFF"
                                doSomething={() => { void handleResend() }}
                            />
                        </div>
                    </div>
                )}
            </form>
        </AnimatedBackgroundContainer>
    )
}

export default VerifyEmailPage
